/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  DICTIONARY_OFFICE_MEASUREMENT_DEFAULT_FOR_EDIT,
  HousePlanRestService,
  OfficeMeasurement,
  PriceBookRestService,
  SystemVarsEstimatingRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import {
  AutocompleteItem,
  AutocompleteItemWithDescription,
  OfficeData,
  withDisplayAsNameAndDescription,
} from '@gh/common-shared-data';
import { Enum, FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { expandObservable } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { keyBy, mapValues } from 'lodash';

import { Observable } from 'rxjs';

import {
  systemVarsEstimateFromDto,
  systemVarsEstimateToDto,
  SystemVarsEstimating,
} from './model/system-vars-estimating.model';

@Injectable()
export class SystemVarsEstimatingBusinessService implements InitViewService {
  private office: OfficeData;

  constructor(private dictionaryService: DictionaryService,
              private restService: SystemVarsEstimatingRestService,
              private priceBookRestService: PriceBookRestService,
              private housePlanRestService: HousePlanRestService,
              private userContext: BuilderUserContext,
  ) {
    this.office = <OfficeData>this.userContext.office;
  }

  initView(): any {
    return {
      sysVarsEstimating: this.restService.get().pipe(map$(systemVarsEstimateFromDto)),
    };
  }

  initCommonSettingsTabView(): any {
    return {
      officeMeasurement: this.dictionaryService.getByEnum(OfficeMeasurement),
      officeMeasurementDefault: expandObservable(mapValues(keyBy(Enum.names(OfficeMeasurement)),
                                                           (name) => this.dictionaryService.getByDef(DICTIONARY_OFFICE_MEASUREMENT_DEFAULT_FOR_EDIT, {
          measurement: name,
        }),
      )),
    };
  }

  initOrdersSettingsTabView(): any {
    return {
      officeMeasurement: this.dictionaryService.getByEnum(OfficeMeasurement),
      officeMeasurementDefault: expandObservable(mapValues(keyBy(Enum.names(OfficeMeasurement)),
                                                           (name) => this.dictionaryService.getByDef(DICTIONARY_OFFICE_MEASUREMENT_DEFAULT_FOR_EDIT, {
          measurement: name,
        }),
      )),
    };
  }

  update(sysVarsEstimating: SystemVarsEstimating): Observable<SystemVarsEstimating> {
    return this.restService.update(systemVarsEstimateToDto(sysVarsEstimating)).pipe(
      map$(systemVarsEstimateFromDto));
  }

  loadPriceBook(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.priceBookRestService.findAllAsAutocompleteDataSource({
        searchString: query.filter,
        showUnreleased: true,
      })
      .select(query).pipe(
        map$(getSlicedData));
  }

  loadHousePlan(query: FilteredDataQuery<string>): Observable<AutocompleteItemWithDescription[]> {
    return this.housePlanRestService.findAllAsAutocompleteDataSource({ searchString: query.filter })
      .select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map(withDisplayAsNameAndDescription)));
  }

}
